// Cookies are host-only and HttpOnly. Browser state never grants Academy access.
import { browserUrl } from "./browserUrl";
import { academyFetch, AcademyApiError } from "./generated/platformTransport";
export { AcademyApiError } from "./generated/platformTransport";

const configured = browserUrl(
  import.meta.env.VITE_ACADEMY_API_BASE_URL || "https://api.academy.chatoner.com",
);
export const apiBase = configured.endsWith("/api") ? configured : `${configured}/api`;
export const tenantSlug = String(import.meta.env.VITE_ACADEMY_TENANT_SLUG || "academy");

async function request<T>(
  path: string,
  body?: unknown,
  method = body === undefined ? "GET" : "POST",
  commandKey?: string,
  signal?: AbortSignal,
): Promise<T> {
  return academyFetch<T>(`${apiBase}/v2/${path}`, {
    method,
    credentials: "include",
    cache: "no-store",
    signal,
    headers:
      body === undefined
        ? { Accept: "application/json", ...(commandKey ? { "Idempotency-Key": commandKey } : {}) }
        : {
            Accept: "application/json",
            "Content-Type": "application/json",
            ...(commandKey ? { "Idempotency-Key": commandKey } : {}),
          },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
}
async function versionedRequest<T>(
  path: string,
  body?: unknown,
  method = body === undefined ? "GET" : "POST",
  commandKey?: string,
  expectedVersion?: number,
  signal?: AbortSignal,
): Promise<T> {
  return academyFetch<T>(`${apiBase}/${path.replace(/^\//, "")}`, {
    method,
    credentials: "include",
    cache: "no-store",
    signal,
    headers: {
      Accept: "application/json",
      ...(body === undefined ? {} : { "Content-Type": "application/json" }),
      ...(commandKey ? { "Idempotency-Key": commandKey } : {}),
      ...(expectedVersion ? { "If-Match": String(expectedVersion) } : {}),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
}
let refresh: Promise<unknown> | undefined;
export async function academyRequest<T>(
  path: string,
  body?: unknown,
  method?: string,
  authenticated = true,
  commandKey?: string,
  signal?: AbortSignal,
): Promise<T> {
  try {
    return await request<T>(path, body, method, commandKey, signal);
  } catch (error) {
    if (!authenticated || !(error instanceof AcademyApiError) || error.status !== 401) throw error;
    refresh ??= request("browser-auth/refresh", {}).finally(() => {
      refresh = undefined;
    });
    await refresh;
    return request<T>(path, body, method, commandKey, signal);
  }
}
export async function academyVersionRequest<T>(
  path: string,
  body?: unknown,
  method?: string,
  authenticated = true,
  commandKey?: string,
  expectedVersion?: number,
  signal?: AbortSignal,
): Promise<T> {
  try {
    return await versionedRequest<T>(path, body, method, commandKey, expectedVersion, signal);
  } catch (error) {
    if (!authenticated || !(error instanceof AcademyApiError) || error.status !== 401) throw error;
    refresh ??= request("browser-auth/refresh", {}).finally(() => {
      refresh = undefined;
    });
    await refresh;
    return versionedRequest<T>(path, body, method, commandKey, expectedVersion, signal);
  }
}
export type BrowserSession = {
  user: { id: string; name: string; email: string };
  tenantId: string;
  workspace: { id: string; name: string };
  roles: string[];
  permissions: string[];
  mfaVerifiedAt: string | null;
};
export const browserSession = () => academyRequest<BrowserSession>("browser-auth/session/bootstrap");
export const logout = () => academyRequest("browser-auth/logout", {}, "POST", false);

// Reuse the command key after an ambiguous network failure. A changed payload is a new command.
export function createCommandSender(options: { headerOnly?: boolean; context?: string } = {}) {
  const keys = new Map<string, string>();
  return <T>(path: string, body: Record<string, unknown>) => {
    const fingerprint = JSON.stringify([options.context || "", path, body]);
    const key = keys.get(fingerprint) || crypto.randomUUID();
    keys.set(fingerprint, key);
    return options.headerOnly
      ? academyRequest<T>(path, body, "POST", true, key)
      : academyRequest<T>(path, { ...body, idempotencyKey: key });
  };
}

export function createVersionedCommandSender(context = "") {
  const keys = new Map<string, string>();
  return <T>(path: string, body: Record<string, unknown>, expectedVersion?: number) => {
    const fingerprint = JSON.stringify([context, path, body, expectedVersion]);
    const key = keys.get(fingerprint) || crypto.randomUUID();
    keys.set(fingerprint, key);
    return academyVersionRequest<T>(path, body, "POST", true, key, expectedVersion);
  };
}
